// Endpoint:  POST /projects
// Schema:    MediaProjectCreation
//
// Required: organization_id, title, type
// Optional: workspace_id, description, deadline, assignee, tone_id,
//           brand_kit_id, contact_id, tags (EntityTag[]), saved_settings_id,
//           storyboard_settings, media_settings.

import { useMemo, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import type { RequestSpec } from '@/types';
import type { PlaygroundProps } from './index';
import { Field, FormCard, PlaygroundPanel, Select, TagsInput, TextArea, TextInput } from './_shared';
import { TONE_IDS, type ToneId } from './_constants';

// Project `type` is a free-form string in the API. The Libertify web app
// and our developer onboarding doc both use `flash` for the canonical
// interactive-experience flow, so it's the default here. The other values
// are kept for backwards-compatible sandboxing.
const PROJECT_TYPES = ['flash', 'Video', 'Document', 'Presentation', 'Podcast'] as const;

const SEQUENCE_STEPS = [
  { id: 'project', label: 'Create project', hint: 'POST /media/projects (you are here)' },
  { id: 'document', label: 'Reserve slot', hint: 'POST /media/projects/{id}/documents/{filename}' },
  { id: 'upload', label: 'PUT bytes', hint: 'Upload the file directly to S3' },
  { id: 'generate', label: 'Generate media', hint: 'POST /media/projects/{id}/media/generate' },
];

export function ProjectPlayground({ mode }: PlaygroundProps) {
  const { organizationId, workspaceId } = useAuth();

  const [title, setTitle] = useState('My first API project');
  const [type, setType] = useState<string>('flash');
  const [description, setDescription] = useState('Created from the API playground');
  const [workspaceIdOverride, setWorkspaceIdOverride] = useState('');
  const [deadline, setDeadline] = useState('');
  const [assignee, setAssignee] = useState<string>('');
  const [toneId, setToneId] = useState<ToneId | ''>('');
  const [brandKitId, setBrandKitId] = useState('');
  const [contactId, setContactId] = useState('');
  const [savedSettingsId, setSavedSettingsId] = useState('');
  const [tags, setTags] = useState<{ tag_key: string; tag_value: string }[]>([
    { tag_key: 'playground', tag_value: 'playground' },
  ]);

  const spec: RequestSpec = useMemo(() => {
    const body: Record<string, unknown> = {
      organization_id: organizationId || '{organization_id}',
      title,
      type,
    };
    if (description) body.description = description;
    const effectiveWorkspaceId = workspaceIdOverride || workspaceId;
    if (effectiveWorkspaceId) body.workspace_id = effectiveWorkspaceId;
    if (deadline) body.deadline = new Date(deadline).toISOString();
    if (assignee) {
      const n = Number(assignee);
      if (Number.isFinite(n)) body.assignee = n;
    }
    if (toneId) body.tone_id = toneId;
    if (brandKitId) body.brand_kit_id = brandKitId;
    if (contactId) body.contact_id = contactId;
    if (savedSettingsId) body.saved_settings_id = savedSettingsId;
    if (tags.length > 0) body.tags = tags;
    return { method: 'POST', path: '/media/projects', body };
  }, [
    organizationId,
    title,
    type,
    description,
    workspaceId,
    workspaceIdOverride,
    deadline,
    assignee,
    toneId,
    brandKitId,
    contactId,
    savedSettingsId,
    tags,
  ]);

  return (
    <PlaygroundPanel
      title="Create a project"
      description='Top-level container. Use type="flash" for the canonical interactive-experience flow. Pass a saved_settings_id (Playbook) to apply a reusable template.'
      spec={spec}
      mode={mode}
      eyebrow="Step 1 of 4 · Create & publish"
      sequence={{ steps: SEQUENCE_STEPS, activeId: 'project' }}
      runLabel="Create project"
    >
      <FormCard>
        <Field label="Organization ID" hint="Set in the top bar — applied automatically">
          <TextInput value={organizationId} disabled placeholder="Set in top bar" />
        </Field>
        <Field label="Title" hint="Required">
          <TextInput value={title} onChange={(e) => setTitle(e.target.value)} required />
        </Field>
        <Field label="Type" hint="Required">
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            {PROJECT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Select>
        </Field>
        <Field label="Description">
          <TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field
          label="Workspace ID"
          hint={
            workspaceId
              ? 'Auto-filled from the top-bar dropdown — paste a different id below to override'
              : 'Optional — defaults to the organization\'s primary workspace'
          }
        >
          <TextInput
            value={workspaceIdOverride}
            onChange={(e) => setWorkspaceIdOverride(e.target.value)}
            placeholder={workspaceId || 'ws_…'}
          />
        </Field>
        <div className="lp-grid lp-grid-cols-1 sm:lp-grid-cols-2 lp-gap-3">
          <Field label="Deadline">
            <TextInput type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          </Field>
          <Field label="Assignee" hint="Numeric user ID">
            <TextInput type="number" value={assignee} onChange={(e) => setAssignee(e.target.value)} />
          </Field>
        </div>
        <Field label="Saved settings (Playbook) ID" hint="Paste an ID returned by the Playbook playground; takes precedence over inline storyboard/media settings">
          <TextInput value={savedSettingsId} onChange={(e) => setSavedSettingsId(e.target.value)} placeholder="ss_…" />
        </Field>
        <div className="lp-grid lp-grid-cols-1 sm:lp-grid-cols-3 lp-gap-3">
          <Field label="Tone">
            <Select value={toneId} onChange={(e) => setToneId(e.target.value as ToneId | '')}>
              <option value="">— None —</option>
              {TONE_IDS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          </Field>
          <Field label="Brand kit ID">
            <TextInput value={brandKitId} onChange={(e) => setBrandKitId(e.target.value)} placeholder="bk_…" />
          </Field>
          <Field label="Contact ID">
            <TextInput value={contactId} onChange={(e) => setContactId(e.target.value)} placeholder="contact_…" />
          </Field>
        </div>
        <Field label="Tags" hint="EntityTag[] — each label is mirrored into tag_key and tag_value">
          <TagsInput value={tags} onChange={setTags} />
        </Field>
      </FormCard>
    </PlaygroundPanel>
  );
}
